import type {
  CoachIdentityIngestionIssue,
  CoachIdentityIngestionReport,
  CoachIdentityReference,
} from "@/lib/behavior/behaviorContracts";
import {
  normalizeCoachIdentityLinkReference,
} from "@/lib/behavior/coachIdentityIngestion";
import {
  coachVoiceDraftToTwinJson,
  type CoachVoiceDraft,
} from "@/lib/behavior/coachVoiceDraft";
import type { FrameworkPackDefinition } from "@/lib/behavior/frameworkPacks";
import {
  parseLinkLines,
  parseList,
} from "@/lib/behavior/coachIdentityWorkspaceShared";
import type {
  CoachLensSource,
  CoachTwinSource,
} from "@/lib/coachSessionReviewContract";
export {
  extractPersistedFrameworkPackSnapshot,
  resolvePersistedVoiceCustomizationStatus,
} from "../../../supabase/functions/_shared/coachVeeraIdentityContext.ts";

function trimText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function toIssueList(
  value: unknown,
): CoachIdentityIngestionIssue[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const candidate = item as Record<string, unknown>;
    const level = candidate.level === "warning" || candidate.level === "error"
      ? candidate.level
      : null;
    const field = trimText(typeof candidate.field === "string" ? candidate.field : null);
    const message = trimText(typeof candidate.message === "string" ? candidate.message : null);
    if (!level || !field || !message) return [];
    return [{ level, field, message }];
  });
}

type UpsertTable = {
  upsert: (
    payload: Record<string, unknown>,
    options?: { onConflict?: string },
  ) => Promise<{ error: unknown }>;
};

type UpdateTable = {
  update: (
    payload: Record<string, unknown>,
  ) => {
    eq: (column: string, value: string) => {
      eq: (column: string, value: string) => Promise<{ error: unknown }>;
    };
  };
};

type CoachIdentityPersistenceClient = {
  from: (table: string) => UpsertTable | UpdateTable;
};

export function extractPersistedCoachIdentityReport(
  value: unknown,
): CoachIdentityIngestionReport | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  const status = candidate.status === "empty"
    || candidate.status === "parsed"
    || candidate.status === "partial"
    || candidate.status === "failed"
    ? candidate.status
    : null;
  const parsedSectionCount = typeof candidate.parsedSectionCount === "number" ? candidate.parsedSectionCount : null;
  const referenceCount = typeof candidate.referenceCount === "number" ? candidate.referenceCount : null;
  const parsedReferenceCount = typeof candidate.parsedReferenceCount === "number" ? candidate.parsedReferenceCount : null;
  if (!status || parsedSectionCount === null || referenceCount === null || parsedReferenceCount === null) {
    return null;
  }

  return {
    status,
    parsedSectionCount,
    referenceCount,
    parsedReferenceCount,
    issues: toIssueList(candidate.issues),
  };
}

export function extractPersistedCoachIdentityReferences(
  coachLens?: CoachLensSource | null,
): CoachIdentityReference[] {
  const links = coachLens?.coach_lens_json?.identity_links;
  if (!Array.isArray(links)) return [];
  return links.map((link) => normalizeCoachIdentityLinkReference({
    id: link?.id,
    title: link?.title,
    sourceUrl: link?.sourceUrl,
    extractedNotes: link?.notes,
  }));
}

export function resolvePersistedCoachIdentityTemplateVersion(
  coachTwin?: CoachTwinSource | null,
): string | null {
  return coachTwin?.identity_template_preview ? "coach_identity_template_v1" : null;
}

export async function saveCoachIdentityLensAndProfile(params: {
  client: CoachIdentityPersistenceClient;
  coachId: string;
  lensName: string;
  lensFrameworksRaw: string;
  preferredLanguageText: string;
  avoidLanguageText: string;
  lensPhilosophy: string;
  linksText: string;
  identityReport: Record<string, unknown>;
  resolvedFrameworkPack: Pick<FrameworkPackDefinition, "id" | "label"> | null;
}) {
  const {
    client,
    coachId,
    lensName,
    lensFrameworksRaw,
    preferredLanguageText,
    avoidLanguageText,
    lensPhilosophy,
    linksText,
    identityReport,
    resolvedFrameworkPack,
  } = params;

  const coachLensTable = client.from("coach_lens") as UpsertTable;
  const { error } = await coachLensTable.upsert(
    {
      coach_id: coachId,
      lens_name: lensName || "Custom coach identity",
      raw_frameworks: lensFrameworksRaw.trim() || null,
      frameworks: parseList(lensFrameworksRaw).map((name) => ({ name, summary: null })),
      preferred_language: parseList(preferredLanguageText),
      avoid_language: parseList(avoidLanguageText),
      coach_lens_json: {
        philosophy: lensPhilosophy.trim() || null,
        frameworks_raw: lensFrameworksRaw.trim() || null,
        framework_pack_id: resolvedFrameworkPack?.id || null,
        framework_pack: resolvedFrameworkPack,
        identity_links: parseLinkLines(linksText),
        identity_report: identityReport,
      },
      is_active: true,
    },
    { onConflict: "coach_id" },
  );
  if (error) throw error;

  const coachProfilesTable = client.from("coach_profiles") as UpsertTable;
  const { error: profileError } = await coachProfilesTable.upsert(
    {
      coach_id: coachId,
      coaching_philosophy: lensPhilosophy.trim() || null,
      frameworks: lensFrameworksRaw.trim() || null,
    },
    { onConflict: "coach_id" },
  );
  if (profileError) throw profileError;
}

export async function saveCoachIdentityVoiceDraft(params: {
  client: CoachIdentityPersistenceClient;
  coachId: string;
  draftId: string;
  draftTwinProfile: CoachVoiceDraft;
  questionStemsText: string;
  phraseBankText: string;
  identityPreview: Record<string, unknown>;
  resolvedFrameworkPack: Pick<FrameworkPackDefinition, "id" | "label"> | null;
  voiceCustomizationStatus: "customized" | "framework_default" | "default";
}) {
  const {
    client,
    coachId,
    draftId,
    draftTwinProfile,
    questionStemsText,
    phraseBankText,
    identityPreview,
    resolvedFrameworkPack,
    voiceCustomizationStatus,
  } = params;

  const normalizedDraft: CoachVoiceDraft = {
    ...draftTwinProfile,
    questionStems: parseList(questionStemsText),
    phraseBank: parseList(phraseBankText),
  };

  const mergedIdentityPreview = {
    ...identityPreview,
    frameworkPackId: resolvedFrameworkPack?.id || null,
    frameworkPackLabel: resolvedFrameworkPack?.label || null,
    voiceCustomizationStatus,
  };

  const coachTwinProfilesTable = client.from("coach_twin_profiles") as UpdateTable;
  const { error } = await coachTwinProfilesTable
    .update({
      twin_json: {
        ...coachVoiceDraftToTwinJson(normalizedDraft),
        identity_template_preview: mergedIdentityPreview,
      },
    })
    .eq("id", draftId)
    .eq("coach_id", coachId);

  if (error) throw error;

  return normalizedDraft;
}
